import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ChevronRight } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from 'src/redux/store';
import { useDataVault } from 'src/contexts/DataVaultContext';
import {
  fetchCertificationSnapshot,
  selectCertificationSnapshot,
} from 'src/redux/slices/DataVault/dataVaultHistoryCertificationDetailSlice';
import {
  selectHistoryCertifications,
  scopeKey as historyScopeKey,
} from 'src/redux/slices/DataVault/dataVaultHistoryCertificationsSlice';
import type {
  SnapshotSection,
  SnapshotPage,
  SnapshotGroup,
} from 'src/api/dataVault/historyCertifications';
import { formatDate } from 'src/components/NewRenterInterface/DataVault/History/components/SnapshotShared';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import { certTypeDisplayLabel } from 'src/components/NewRenterInterface/utils/certTerminology';
import { CertificationSnapshotDetailSkeleton } from './CertificationSnapshotDetailSkeleton';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function countAnswers(section: SnapshotSection): number {
  let count = 0;
  section.pages.forEach((p: SnapshotPage) => {
    p.groups.forEach((g: SnapshotGroup) => {
      count += g.answers.length;
    });
  });
  return count;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SectionCard({
  section,
  to,
}: {
  section: SnapshotSection;
  to: string;
}): React.ReactElement {
  const answerCount = countAnswers(section);
  const theme = useTheme();

  return (
    <Box
      component={Link}
      to={to}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'background.paper',
        borderRadius: '16px',
        boxShadow: '0 1px 8px rgba(0,0,0,0.03)',
        py: '14px',
        px: 2,
        textDecoration: 'none',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack spacing={0.25}>
        <Typography
          sx={{
            color: 'text.primary',
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {section.name}
        </Typography>
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: 12,
          }}
        >
          {answerCount} {answerCount === 1 ? 'answer' : 'answers'}
        </Typography>
      </Stack>
      <ChevronRight size={18} color={theme.palette.text.secondary} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CertificationSnapshotDetailPage(): React.ReactElement {
  const dispatch = useDispatch<AppDispatch>();
  const { actingOnMemberId } = useDataVault();
  const { certificationId } = useParams<{ certificationId: string }>();
  const certId = certificationId ? parseInt(certificationId, 10) : NaN;

  const entry = useSelector(
    selectCertificationSnapshot(actingOnMemberId, certId)
  );

  // Look up the cert in the already-loaded history list so we can show the
  // property name in the header while the snapshot is still loading.
  const historyEntry = useSelector(
    selectHistoryCertifications(historyScopeKey(actingOnMemberId))
  );
  const summaryCert = historyEntry?.certifications?.find(
    (c) => c.id === certId
  );
  const knownTitle = summaryCert?.property_name ?? '';

  useEffect(() => {
    if (!certId || isNaN(certId)) {
      return;
    }
    if (
      entry.status === 'loading' ||
      entry.status === 'succeeded' ||
      entry.status === 'failed'
    ) {
      return;
    }
    dispatch(
      fetchCertificationSnapshot({ certificationId: certId, actingOnMemberId })
    );
  }, [dispatch, certId, actingOnMemberId, entry.status]);

  const backPath = actingOnMemberId
    ? `/renter/data-vault/members/${actingOnMemberId}`
    : '/renter/data-vault';

  function sectionPath(sectionId: number): string {
    return actingOnMemberId
      ? `/renter/data-vault/members/${actingOnMemberId}/history/certifications/${certId}/sections/${sectionId}`
      : `/renter/data-vault/history/certifications/${certId}/sections/${sectionId}`;
  }

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  if (!certId || isNaN(certId)) {
    return (
      <Box>
        <PageHeader
          title="Application / Re-certification"
          backRoute={backPath}
        />
        <Box sx={{ p: 2 }}>
          <Typography
            role="alert"
            sx={{ color: 'text.secondary', fontSize: 14 }}
          >
            Invalid application or re-certification.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (entry.status === 'loading' || entry.status === 'idle') {
    return (
      <CertificationSnapshotDetailSkeleton
        backRoute={backPath}
        title={knownTitle}
      />
    );
  }

  if (entry.status === 'failed' || !entry.data) {
    return (
      <Box>
        <PageHeader
          title="Application / Re-certification"
          backRoute={backPath}
        />
        <Box sx={{ p: 2 }}>
          <Typography
            role="alert"
            sx={{ color: 'text.secondary', fontSize: 14 }}
          >
            {entry.error ??
              'Failed to load application or re-certification snapshot.'}{' '}
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                dispatch(
                  fetchCertificationSnapshot({
                    certificationId: certId,
                    actingOnMemberId,
                    forceRefresh: true,
                  })
                )
              }
              sx={{ textTransform: 'none', ml: 1 }}
            >
              Retry
            </Button>
          </Typography>
        </Box>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Content
  // ---------------------------------------------------------------------------

  const { certification, sections } = entry.data;

  const subtitle = [
    certTypeDisplayLabel(certification.cert_type_label),
    certification.effective_date
      ? formatDate(certification.effective_date)
      : null,
  ]
    .filter(Boolean)
    .join(' \u00B7 ');

  return (
    <Box
      sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: '100px' }}
    >
      <PageHeader
        title={certification.property_name || '\u2014'}
        subtitle={subtitle || undefined}
        backRoute={backPath}
      />

      {/* Section cards */}
      {sections.length === 0 ? (
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: 14,
            textAlign: 'center',
            mt: 4,
            px: 2,
          }}
        >
          No answers recorded for this application or re-certification.
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ px: 2 }}>
          {sections.map((s) => (
            <SectionCard key={s.id} section={s} to={sectionPath(s.id)} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
